/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, {
  useRef,
  cloneElement,
  type ReactElement,
  type Ref,
  type RefObject,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import type { CSSTransitionProps } from 'react-transition-group/CSSTransition';

/**
 * A CSSTransition that owns the ref to the DOM node it animates.
 *
 * Without `nodeRef`, react-transition-group finds the node with
 * `findDOMNode`, which React 19 removes. Every item in a list needs its own
 * ref, so the ref lives here and not in the parent.
 *
 * `children` is either a DOM element, which gets the ref, or a function that
 * receives the ref and returns the element. Use the function for components
 * and pass the ref on as `rootRef`. All other props go to CSSTransition, which
 * is also how TransitionGroup injects `in` and `onExited`.
 */
// Omit on the union would collapse it, and CSSTransition needs `timeout` or
// `addEndListener`, so omit from each member.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type NodeTransitionProps = DistributiveOmit<
  CSSTransitionProps<HTMLElement>,
  'children' | 'nodeRef'
> & {
  children:
    | ReactElement<{ ref?: Ref<HTMLElement> }>
    | ((nodeRef: RefObject<HTMLElement | null>) => ReactElement);
};

function NodeTransition({ children, ...props }: NodeTransitionProps) {
  const nodeRef = useRef<HTMLElement>(null);

  return (
    <CSSTransition
      {...(props as CSSTransitionProps<HTMLElement>)}
      nodeRef={nodeRef}
    >
      {typeof children === 'function'
        ? children(nodeRef)
        : cloneElement(children, { ref: nodeRef })}
    </CSSTransition>
  );
}

export default NodeTransition;
